import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, MessageSquare, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AtendimentoMediaBubble } from './AtendimentoMediaBubble';
import { AtendimentoComposer } from './AtendimentoComposer';
import { AtendimentoMessageMeta } from './AtendimentoMessageMeta';
import { DemandaClienteResumoCard } from './DemandaClienteResumoCard';
import { ChatMsg, DemandaClienteResumo, PortalConversation } from './demandaTypes';

type Props = {
  companyId?: string;
};

function roleLabel(role: string): string {
  if (role === 'USER') return 'Cliente';
  if (role === 'ASSISTANT') return 'Rique.Ia';
  if (role === 'OPERATOR') return 'Operador';
  if (role === 'SYSTEM') return 'Sistema';
  return role;
}

export function DemandasChatPanel({ companyId }: Props) {
  const [conversations, setConversations] = useState<PortalConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [loading, setLoading] = useState(false);
  const [clienteResumo, setClienteResumo] = useState<DemandaClienteResumo | null>(null);
  const [clienteResumoLoading, setClienteResumoLoading] = useState(false);
  const unreadSumRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;
  const showClientePanel = selected?.actorType === 'CLIENTE';

  const loadConversations = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const res = await api.get('/rique-atendimento/portal/conversations', {
        params: companyId ? { companyId } : {},
      });
      const list = (res.data?.data ?? []) as PortalConversation[];
      const next = Array.isArray(list) ? list : [];
      const sum = next.reduce((a, c) => a + (c.operatorUnreadCount ?? 0), 0);
      if (unreadSumRef.current > 0 && sum > unreadSumRef.current) {
        toast('Nova mensagem no chat de atendimento', { icon: '💬' });
      }
      unreadSumRef.current = sum;
      setConversations(next);
      if (next.length && !selectedId) setSelectedId(next[0]!.id);
    } catch (e: any) {
      if (!opts?.silent) toast.error(e?.response?.data?.message || 'Falha ao listar chats');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [companyId, selectedId]);

  const loadMessages = useCallback(async (id: string) => {
    try {
      const res = await api.get(`/rique-atendimento/portal/conversations/${id}/messages`);
      setMessages(res.data?.data ?? []);
    } catch {
      /* poll silencioso */
    }
  }, []);

  const loadClienteResumo = useCallback(async (conversation: PortalConversation) => {
    if (conversation.actorType !== 'CLIENTE') {
      setClienteResumo(null);
      setClienteResumoLoading(false);
      return;
    }
    setClienteResumoLoading(true);
    try {
      const res = await api.get(
        `/rique-atendimento/portal/conversations/${conversation.id}/cliente-resumo`
      );
      setClienteResumo((res.data?.data ?? null) as DemandaClienteResumo | null);
    } catch {
      setClienteResumo(null);
    } finally {
      setClienteResumoLoading(false);
    }
  }, []);

  const selectConversation = async (id: string) => {
    setSelectedId(id);
    const conv = conversations.find((c) => c.id === id);
    await api
      .post(`/rique-atendimento/portal/conversations/${id}/mark-read`)
      .catch(() => undefined);
    await loadMessages(id);
    if (conv) void loadClienteResumo(conv);
    void loadConversations({ silent: true });
  };

  useEffect(() => {
    void loadConversations();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setInterval(() => void loadConversations({ silent: true }), 15_000);
    return () => clearInterval(t);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setClienteResumo(null);
      return;
    }
    void loadMessages(selectedId);
    const conv = conversations.find((c) => c.id === selectedId);
    if (conv) void loadClienteResumo(conv);
    const t = setInterval(() => void loadMessages(selectedId), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, loadMessages, loadClienteResumo]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const takeover = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/rique-atendimento/portal/conversations/${selectedId}/takeover`);
      toast.success('Você assumiu o atendimento');
      await loadConversations();
      await loadMessages(selectedId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha no takeover');
    }
  };

  const release = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/rique-atendimento/portal/conversations/${selectedId}/release`);
      toast.success('Atendimento devolvido à IA');
      await loadConversations();
      await loadMessages(selectedId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao liberar');
    }
  };

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-3 h-[min(72vh,640px)] min-h-[480px] ${
        showClientePanel ? 'xl:grid-cols-[280px_minmax(0,1fr)_300px]' : ''
      }`}
    >
      {/* Lista */}
      <div className="border rounded-lg overflow-hidden flex flex-col bg-background min-h-0">
        <div className="shrink-0 px-3 py-2 border-b text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Conversas
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void loadConversations()}
            disabled={loading}
          >
            Atualizar
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {conversations.map((c) => {
            const unread = c.operatorUnreadCount ?? 0;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => void selectConversation(c.id)}
                className={`w-full text-left px-3 py-3 border-b hover:bg-muted/50 relative ${
                  selectedId === c.id ? 'bg-muted' : ''
                } ${unread > 0 ? 'bg-primary/5' : ''}`}
              >
                {unread > 0 ? (
                  <span className="absolute top-2 right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : null}
                <div className="text-sm font-medium truncate pr-6">{c.title || 'Atendimento'}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {c.company?.name || c.companyId}
                  {c.actorType === 'CLIENTE' ? ' · Cliente' : ''}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  <Badge variant={c.mode === 'HUMAN' ? 'warning' : 'info'}>
                    {c.mode === 'HUMAN' ? 'Humano' : 'IA'}
                  </Badge>
                  {c.demandas?.[0] ? (
                    <Badge variant="outline">{c.demandas[0].protocolo}</Badge>
                  ) : null}
                </div>
              </button>
            );
          })}
          {!conversations.length && !loading ? (
            <p className="text-xs text-muted-foreground text-center py-8">Nenhuma conversa aberta.</p>
          ) : null}
        </div>
      </div>

      {/* Chat */}
      <div className="border rounded-lg flex flex-col min-h-0 bg-background overflow-hidden">
        {selected ? (
          <>
            <div className="shrink-0 px-3 py-2 border-b flex flex-wrap items-center gap-2 justify-between">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{selected.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {selected.company?.name} · {selected.actorType}
                  {selected.demandas?.[0] ? ` · ${selected.demandas[0].protocolo}` : ''}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => void takeover()}>
                  <UserCheck className="w-3 h-3 mr-1" />
                  Assumir
                </Button>
                <Button size="sm" variant="outline" onClick={() => void release()}>
                  <Bot className="w-3 h-3 mr-1" />
                  Devolver IA
                </Button>
              </div>
            </div>

            {/* Contexto no mobile / lg sem coluna xl */}
            {showClientePanel ? (
              <div className="xl:hidden shrink-0 max-h-[40%] overflow-y-auto border-b p-2">
                <DemandaClienteResumoCard data={clienteResumo} loading={clienteResumoLoading} />
              </div>
            ) : null}

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2 bg-muted/20">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${
                    m.role === 'USER'
                      ? 'justify-end'
                      : m.role === 'SYSTEM'
                        ? 'justify-center'
                        : 'justify-start'
                  }`}
                >
                  <div
                    className={`text-sm rounded-lg px-3 py-2 max-w-[min(100%,28rem)] ${
                      m.role === 'USER'
                        ? 'bg-slate-900 text-white'
                        : m.role === 'OPERATOR'
                          ? 'bg-primary/15 border border-primary/30'
                          : m.role === 'SYSTEM'
                            ? 'bg-amber-50 text-amber-900 text-xs'
                            : 'bg-white border'
                    }`}
                  >
                    <div className="text-[10px] opacity-60 mb-0.5">{roleLabel(m.role)}</div>
                    <AtendimentoMediaBubble message={m} conversationId={selected.id} />
                    <AtendimentoMessageMeta
                      createdAt={m.createdAt}
                      role={m.role}
                      readByPeer={m.readByPeer}
                      portal
                    />
                  </div>
                </div>
              ))}
              {!messages.length ? (
                <p className="text-xs text-muted-foreground text-center py-8">Sem mensagens.</p>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
            <div className="shrink-0 bg-background">
              <AtendimentoComposer
                conversationId={selected.id}
                showQuickActions
                isCliente={selected.actorType === 'CLIENTE'}
                onSent={async () => {
                  await loadMessages(selected.id);
                  await loadConversations({ silent: true });
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Selecione uma conversa
          </div>
        )}
      </div>

      {/* Contexto desktop */}
      {showClientePanel ? (
        <div className="hidden xl:flex border rounded-lg bg-background min-h-0 overflow-y-auto p-3">
          <div className="w-full">
            <DemandaClienteResumoCard data={clienteResumo} loading={clienteResumoLoading} />
            {!clienteResumoLoading && (!clienteResumo || !clienteResumo.available) ? (
              <p className="text-xs text-muted-foreground mt-2">
                Contexto financeiro indisponível para este solicitante.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
